/**
 * CSV parsing for bulk manual ingestion.
 *
 * Deliberately hand-rolled rather than `split(",")`: the `text` column carries whole
 * articles, so commas, double quotes and newlines inside a quoted field are the norm,
 * not the edge case. A naive split silently shreds every multi-paragraph row into
 * dozens of malformed ones, which is worse than refusing the file.
 *
 * Kept free of React so the rules that decide what the server will accept can be
 * unit-tested without rendering anything.
 */

/** Columns the manual ingestion endpoint understands. Mirrors ManualIngestionRequest. */
export const CONTENT_COLUMNS = [
  "content_type",
  "title",
  "author",
  "text",
  "source_url",
  "publish_date",
] as const;

export type ContentColumn = (typeof CONTENT_COLUMNS)[number];

/** Columns without which a row cannot be ingested at all. */
export const REQUIRED_COLUMNS: ContentColumn[] = [
  "content_type",
  "title",
  "author",
  "text",
];

/**
 * Upper bound on rows accepted from one file. Mirrors MAX_BULK_ROWS in
 * server/pipeline_ingestion/serializers.py — the browser should say "too big" before
 * a multi-megabyte POST is built and rejected.
 */
export const MAX_CSV_ROWS = 200;

/** Refuse absurd files before reading them into memory. */
export const MAX_CSV_BYTES = 10 * 1024 * 1024;

export interface CsvRow {
  /** 1-based position among data rows, excluding the header. */
  index: number;
  values: Record<ContentColumn, string>;
}

export interface ParsedContentCsv {
  rows: CsvRow[];
  /** Header names that are not ingestion columns; parsed but ignored. */
  ignoredHeaders: string[];
  /** Set when nothing usable could be read; `rows` is empty when present. */
  error?: string;
}

/**
 * Split raw CSV text into records of fields, following RFC 4180.
 *
 * Handles quoted fields containing `,`, `"` (escaped as `""`) and line breaks, both
 * CRLF and LF endings, and strips the UTF-8 BOM that Excel prepends on export.
 */
export function splitCsvRecords(input: string): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;

  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    record.push(field);
    field = "";
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  while (i < text.length) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    // A quote only opens a field when it is the first character of that field;
    // anywhere else it is treated as literal text, which is friendlier than
    // failing on hand-edited files.
    if (char === '"' && field === "") {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === ",") {
      endField();
      i += 1;
      continue;
    }
    if (char === "\r") {
      endRecord();
      i += text[i + 1] === "\n" ? 2 : 1;
      continue;
    }
    if (char === "\n") {
      endRecord();
      i += 1;
      continue;
    }

    field += char;
    i += 1;
  }

  endRecord();

  // A file ending in a newline produces one trailing empty record; so do stray blank
  // lines between rows. Neither is data.
  return records.filter((r) => r.some((value) => value.trim() !== ""));
}

/** `Publish Date` and `publish-date` both mean `publish_date`. */
function normaliseHeader(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isContentColumn(name: string): name is ContentColumn {
  return (CONTENT_COLUMNS as readonly string[]).includes(name);
}

/**
 * Parse a CSV export into ingestion rows.
 *
 * Column order does not matter and unknown columns are ignored, so a spreadsheet
 * carrying extra bookkeeping columns can be uploaded as-is.
 */
export function parseContentCsv(input: string): ParsedContentCsv {
  const empty: ParsedContentCsv = { rows: [], ignoredHeaders: [] };

  const records = splitCsvRecords(input);
  if (records.length === 0) {
    return { ...empty, error: "That file is empty." };
  }

  const [headerRecord, ...dataRecords] = records;
  const normalised = headerRecord.map(normaliseHeader);

  const headers: ContentColumn[] = [];
  const ignoredHeaders: string[] = [];
  const duplicates: string[] = [];
  // Column index -> ingestion column, for columns we keep.
  const columnAt = new Map<number, ContentColumn>();

  normalised.forEach((name, position) => {
    if (!isContentColumn(name)) {
      if (headerRecord[position].trim()) ignoredHeaders.push(headerRecord[position].trim());
      return;
    }
    if (headers.includes(name)) {
      duplicates.push(name);
      return;
    }
    headers.push(name);
    columnAt.set(position, name);
  });

  if (duplicates.length > 0) {
    return {
      ...empty,
      error: `Duplicate column${duplicates.length > 1 ? "s" : ""}: ${duplicates.join(", ")}. Each column may appear once.`,
    };
  }

  if (headers.length === 0) {
    return {
      ...empty,
      error:
        "No recognised columns in the header row. Expected at least: " +
        REQUIRED_COLUMNS.join(", ") +
        ". Download the template for the exact format.",
    };
  }

  const missingHeaders = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));
  if (missingHeaders.length > 0) {
    return {
      ...empty,
      ignoredHeaders,
      error: `Missing required column${missingHeaders.length > 1 ? "s" : ""}: ${missingHeaders.join(", ")}.`,
    };
  }

  if (dataRecords.length === 0) {
    return { ...empty, ignoredHeaders, error: "That file has a header row but no data rows." };
  }

  if (dataRecords.length > MAX_CSV_ROWS) {
    return {
      ...empty,
      ignoredHeaders,
      error: `That file has ${dataRecords.length} rows; the maximum is ${MAX_CSV_ROWS} per upload. Split it into smaller files.`,
    };
  }

  const rows: CsvRow[] = dataRecords.map((record, position) => {
    const values = Object.fromEntries(
      CONTENT_COLUMNS.map((column) => [column, ""]),
    ) as Record<ContentColumn, string>;

    columnAt.forEach((column, columnIndex) => {
      values[column] = record[columnIndex] ?? "";
    });

    return { index: position + 1, values };
  });

  return { rows, ignoredHeaders };
}
